import { Heading5 } from '@blueprint/ui';
import { ArrowRightSmall } from '@blueprint/icons';
import { ListingImage } from '@zoopla/listings/image';
import { ListingFeatureList } from '@zoopla/listings/features';
import {
    HeadingRow,
    StyledListingCardScrollRow
} from './SavedListingsWidgetDesktop.styles';
import {
    StyledListingCardPortraitDetails,
    StyledListingCardPortraitAddress,
    StyledListingCardPortraitPrice,
    StyledListingCardPortraitTitle
} from '@zoopla/listings/cards';
import {
    trackClickSavedListingWidgetCard,
    trackClickSeeAllSavedListings
} from '../SavedListingsWidget.tracking';
import {
    TriggerWhenVisible,
    ListingCardPortrait,
    ListingCardPortraitSkeleton
} from '@zoopla/listings/cards';
import { MappedEnrichedSavedPropertyId } from '../graphql/EnrichedSavedPropertyIds/useEnrichedSavedPropertyIds';
import { useListing } from '../graphql/Listing/useListing';
import { getBadgeForSavedListing } from '@zoopla/listing-badges';
import { Badge } from '@zoopla-eng/zds-components/badges';
import { ButtonRight } from '@zoopla-eng/zds-components/button';
import { Box } from '@zoopla-eng/zds-components/box';

export const SavedListingCardDesktop = ({
    listingId,
    isTriggered
}: {
    listingId: string;
    isTriggered: boolean;
}) => {
    const listing = useListing(listingId, !isTriggered);

    if (!listing) {
        return <ListingCardPortraitSkeleton />;
    }

    const {
        imagePreview,
        featurePreview,
        displayAddress,
        listingLinkHref,
        displayPrice,
        title,
        publicationStatus,
        section,
        statusSummary,
        tags
    } = listing;

    const badgeProps = getBadgeForSavedListing({
        isExpired: publicationStatus === 'Expired',
        isNewBuild: section === 'new-homes',
        statusSummaryLabel: statusSummary?.label,
        tagLabels: tags?.map(({ label }) => label)
    });

    return (
        <ListingCardPortrait
            testId="saved-property-card-desktop"
            listingLinkHref={listingLinkHref}
            onClick={trackClickSavedListingWidgetCard}
        >
            <ListingImage image={imagePreview} useImageLazyLoad={true} />
            <StyledListingCardPortraitDetails
                data-testid={`saved-property-card-details-${listingId}`}
            >
                {badgeProps ? (
                    <Box pb="$S200">
                        <Badge {...badgeProps} />
                    </Box>
                ) : null}

                <StyledListingCardPortraitTitle>
                    {title}
                </StyledListingCardPortraitTitle>
                <StyledListingCardPortraitPrice>
                    {displayPrice}
                </StyledListingCardPortraitPrice>
                <StyledListingCardPortraitAddress>
                    {displayAddress}
                </StyledListingCardPortraitAddress>
                <ListingFeatureList features={featurePreview} />
            </StyledListingCardPortraitDetails>
        </ListingCardPortrait>
    );
};

export const SavedListingsWidgetDesktop = ({
    listings
}: {
    listings: Array<MappedEnrichedSavedPropertyId>;
}) => {
    return (
        <div
            data-testid="saved-property-widget"
            aria-labelledby="saved-properties-widget-header"
        >
            <HeadingRow>
                <Heading5 size="6" id="saved-properties-widget-header">
                    Recently saved properties
                </Heading5>
                <ButtonRight
                    category="tertiary"
                    icon={<ArrowRightSmall />}
                    data-testid="see-all-saved-properties"
                    onClick={trackClickSeeAllSavedListings}
                    {...{ as: 'a', href: '/favourites/properties/' }}
                >
                    See all saved properties
                </ButtonRight>
            </HeadingRow>
            <StyledListingCardScrollRow>
                {listings.map(({ listingId }) => {
                    return (
                        <TriggerWhenVisible triggerDelay={100} key={listingId}>
                            {(isTriggered: boolean) => {
                                return (
                                    <SavedListingCardDesktop
                                        listingId={listingId}
                                        isTriggered={isTriggered}
                                    />
                                );
                            }}
                        </TriggerWhenVisible>
                    );
                })}
            </StyledListingCardScrollRow>
        </div>
    );
};
